import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  loadEntityConfig, 
  getListViewFields, 
  getListViewTabs,
  getDetailViewFields,
  getFormViewFields,
  isFieldVisible,
  getFieldDisplayValue,
  getFieldBadge,
  clearConfigCache
} from '../utils/entityConfig';

/**
 * Custom hook to load and use entity configuration
 * @param {string} entityName - Name of the entity
 * @returns {object} Entity config and helper methods
 */
export function useEntityConfig(entityName) {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const normalizedEntityName = entityName ? entityName.toLowerCase() : null;

  const loadConfig = useCallback(async (signal = null) => {
    if (!normalizedEntityName) return;

    try {
      setLoading(true);
      setError(null);

      const tenantCode = user?.selectedTenant?.code ||
                        user?.selectedTenant?.tenant_code ||
                        user?.tenantCode ||
                        null;
      const entityConfig = await loadEntityConfig(normalizedEntityName, tenantCode, { signal });

      if (signal?.aborted) return;
      if (entityConfig) {
        setConfig(entityConfig);
      } else {
        setError(`Configuration not found for ${entityName}`);
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error loading entity config:', err);
      setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [normalizedEntityName, user?.selectedTenant?.code, user?.selectedTenant?.tenant_code, user?.tenantCode, entityName]);

  useEffect(() => {
    const controller = new AbortController();
    loadConfig(controller.signal);
    return () => controller.abort();
  }, [loadConfig]);

  useEffect(() => {
    if (!normalizedEntityName) return;

    const handleConfigUpdated = (event) => {
      const updatedEntity = event.detail?.entityName;
      if (updatedEntity && updatedEntity === normalizedEntityName) {
        clearConfigCache(normalizedEntityName);
        loadConfig(null);
      }
    };

    window.addEventListener('entity-config-updated', handleConfigUpdated);
    return () => {
      window.removeEventListener('entity-config-updated', handleConfigUpdated);
    };
  }, [normalizedEntityName, loadConfig]);

  // Stable helper refs so consumers (e.g. useEntityTableColumns) don't get new refs every render and trigger infinite useEffect
  const getListViewFieldsStable = useCallback((options = {}) => config ? getListViewFields(config, options) : [], [config]);
  const getListViewTabsStable = useCallback(() => config ? getListViewTabs(config) : [], [config]);
  const getDetailViewFieldsStable = useCallback(() => config ? getDetailViewFields(config) : [], [config]);
  const getFormViewFieldsStable = useCallback(() => config ? getFormViewFields(config) : [], [config]);
  const isFieldVisibleStable = useCallback((field, data) => isFieldVisible(field, data), []);
  const getFieldDisplayValueStable = useCallback((field, value, data) => getFieldDisplayValue(field, value, data), []);
  const getFieldBadgeStable = useCallback((field, value) => getFieldBadge(field, value), []);

  return {
    config,
    loading,
    error,
    getListViewFields: getListViewFieldsStable,
    getListViewTabs: getListViewTabsStable,
    getDetailViewFields: getDetailViewFieldsStable,
    getFormViewFields: getFormViewFieldsStable,
    isFieldVisible: isFieldVisibleStable,
    getFieldDisplayValue: getFieldDisplayValueStable,
    getFieldBadge: getFieldBadgeStable
  };
}

/**
 * Custom hook for dynamic table columns based on entity config
 * @param {string} entityName - Name of the entity
 * @returns {object} Columns and helper methods
 */
export function useEntityTableColumns(entityName) {
  const { config, loading, error, getListViewFields } = useEntityConfig(entityName);
  const [columns, setColumns] = useState([]);
  const hasLoggedLoadingRef = useRef(false);

  useEffect(() => {
    if (config && getListViewFields) {
      try {
        const fields = getListViewFields();
        // Only log in development mode - reduced verbosity
        if (process.env.NODE_ENV === 'development' && fields.length > 0) {
          console.log(`✅ [useEntityTableColumns] ${entityName}: ${fields.length} fields ready`);
        }
        
        if (!Array.isArray(fields)) {
          console.error(`❌ [useEntityTableColumns] ${entityName}: getListViewFields() did not return an array:`, typeof fields);
          setColumns([]);
          return;
        }
        
        const tableColumns = fields.map(field => ({
          key: field.name,
          label: field.displayName || field.name,
          field: field,
          sortable: true,
          visible: true
        }));
        setColumns(tableColumns);
        hasLoggedLoadingRef.current = false; // Reset for next load cycle
      } catch (err) {
        console.error(`❌ [useEntityTableColumns] ${entityName}: Error generating columns:`, err);
        setColumns([]);
      }
    } else {
      // Only log loading state ONCE per load cycle to prevent console spam
      if (!hasLoggedLoadingRef.current && process.env.NODE_ENV === 'development' && !loading) {
        console.log(`⏳ [useEntityTableColumns] ${entityName}: Waiting for config...`);
        hasLoggedLoadingRef.current = true;
      }
      setColumns([]);
    }
  }, [config, getListViewFields, entityName, loading]);

  return {
    columns,
    loading,
    error,
    config
  };
}

/**
 * Custom hook for dynamic form fields based on entity config
 * @param {string} entityName - Name of the entity
 * @param {object} data - Current form data for conditional visibility
 * @returns {object} Form fields and helper methods
 */
export function useEntityFormFields(entityName, data = {}) {
  const { config, loading, error, getFormViewFields, isFieldVisible } = useEntityConfig(entityName);
  const [fields, setFields] = useState([]);
  const [sections, setSections] = useState({});

  useEffect(() => {
    if (config) {
      const formFields = getFormViewFields();
      
      // Filter visible fields
      const visibleFields = formFields.filter(field => 
        isFieldVisible(field, data)
      );
      
      // Group by section
      const grouped = {};
      visibleFields.forEach(field => {
        const section = field.section || field.group || 'General';
        if (!grouped[section]) {
          grouped[section] = [];
        }
        grouped[section].push(field);
      });
      
      setFields(visibleFields);
      setSections(grouped);
    }
  }, [config, data]);

  return {
    fields,
    sections,
    loading,
    error,
    config
  };
}

export default useEntityConfig;

